import React, { useImperativeHandle, useRef } from 'react';
import QRCode from 'qrcode.react';
import { DATE_TIME_FORMAT } from '@/config';
import { renderIntlLabel } from '@/utils';
import { parseFen2Currency, parseFen2Yuan } from '@/utils/number';
import { getTimeByUtcOffset, TenantSignStorage } from '@/utils/intl';
import paymentBillSrc from '../imgs/payment_bill.png';
import sloganSrc from '../imgs/slogan.png';
import bg from '../imgs/bg.png';
import wyImgSrc from '../imgs/wywkLog.png';
import { InviteDetail } from '../helper';

import styles from './index.module.scss';

interface InviteContentRef {
  getChildrenRef: () => React.ReactElement;
}

interface InviteContentProps {
  detail?: InviteDetail;
}
const InviteContent = React.forwardRef<InviteContentRef, InviteContentProps>(({ detail }, ref) => {
  const containerRef = useRef<any>();
  const scanCodeRef = useRef<any>();

  // 商户名称
  const merchantName = TenantSignStorage.getLocalConfig()?.marchantName || '--';
  const joinTel = TenantSignStorage.getLocalConfig()?.joinTel || '--';
  const serviceTel = TenantSignStorage.getLocalConfig()?.serviceTel || '--';

  useImperativeHandle(
    ref,
    () => ({
      getChildrenRef: () => containerRef.current,
      getScanCodeRef: () => scanCodeRef.current,
    }),
    [],
  );

  return (
    <>
      {/* 屏幕外显示所有，包含二维码 */}

      {/* position: 'fixed', */}
      <div
        className={styles.billContent}
        ref={containerRef}
        style={{ position: 'fixed', left: '-50000px', bottom: '-50000px', zIndex: -10000 }}
      >
        <div className={styles.billContentBg}>
          <img src={bg} alt="" />
        </div>

        <div className={styles.contentBox}>
          <div className={styles.title}>
            <img src={wyImgSrc} alt="logo" crossOrigin="anonymous" />
            <div>{renderIntlLabel('order_crs_title_hotel_book', { merchantName })}</div>
          </div>
          {/* <div className={styles.subject}>网鱼官方预订邀请单</div> */}

          {/* 支付详情 */}
          <div className={styles.orderDetail}>
            <div className={styles.name}>{detail?.name}</div>
            <div className={`${styles.orderDetailItem} ${styles.orderDetailItemFirst}`}>
              <div className={styles.detailLabel}>
                {renderIntlLabel('order_crs_label_actually_room_expense')}
              </div>
              <div className={styles.detailContent}>
                ¥{parseFen2Yuan(detail?.orderRealPayAmount)}
                {(!!detail?.availablePoints || !!detail?.pointsDeductionAmount) && (
                  <div className={styles.points}>{`${renderIntlLabel(
                    'channel_hotel_has_used_amount_of_points',
                    {
                      availablePoints: detail?.availablePoints,
                    },
                  )}${renderIntlLabel('channel_hotel_pointsDeductionAmount', {
                    money: `¥${parseFen2Yuan(detail?.pointsDeductionAmount)}`,
                  })}`}</div>
                )}
              </div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {renderIntlLabel('order_roomorder_subscriber')}
              </div>
              <div className={styles.detailContent}>{detail?.userName}</div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>{renderIntlLabel('order_roomorder_mobile')}</div>
              <div className={styles.detailContent}>{detail?.userPhone}</div>
            </div>
          </div>

          {/* 二维码 */}
          <div className={styles.codeScan}>
            <div className={styles.pointing}>
              {renderIntlLabel('order_crs_tip_scan_by_wechat')}
              <br />
              {renderIntlLabel('order_crs_tip_scan_to_confirm_to_pay')}
            </div>
            <div className={styles.code} ref={scanCodeRef}>
              <QRCode value={detail?.orderUrl} size={155} />
            </div>

            <div className={styles.prompt}>
              {renderIntlLabel('order_crs_tip_contact_theFrontDesk')}
            </div>
            <div className={styles.promptTip}>
              {renderIntlLabel('order_crs_tip_search_miniProgram_by_wechat')}
              <br />
              {renderIntlLabel('order_crs_tip_download_app')}
            </div>
          </div>

          {/* 订单详情 */}
          <div className={styles.orderDetail}>
            <div className={styles.itemTitle}>
              {renderIntlLabel('order_goods_label_order_info')}
            </div>

            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {renderIntlLabel('order_crs_label_store_address')}
              </div>
              <div className={styles.detailContent}>{detail?.address}</div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {renderIntlLabel('order_crs_label_store_mobile')}
              </div>
              <div className={styles.detailContent}>{detail?.storePhone}</div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {renderIntlLabel('order_selfService_order_no')}
              </div>
              <div className={styles.detailContent}>{detail?.orderNo}</div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {renderIntlLabel('order_crs_label_print_order_time')}
              </div>
              <div className={styles.detailContent}>
                {detail?.operateTime
                  ? getTimeByUtcOffset(detail?.operateTime, DATE_TIME_FORMAT)
                  : ''}
              </div>
            </div>
            <div className={styles.orderDetailItem}>
              <div className={styles.detailLabel}>
                {renderIntlLabel('order_crs_label_print_person')}
              </div>
              <div className={styles.detailContent}>{detail?.operator}</div>
            </div>
          </div>

          {/* 加盟热线 */}
          <div>
            <img className={styles.slogan} src={sloganSrc} alt="" />
            <div className={styles.hotline}>
              <div className={styles.serviceHotline}>
                {renderIntlLabel('order_crs_label_wy_service_hot_line')}
                <span>{serviceTel}</span>
              </div>
              <div className={styles.leagueHotline}>
                {renderIntlLabel('order_crs_label_wy_join_hot_line')}
                <span>{joinTel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 静态图片，预览 */}
      <img
        className={styles.previewImg}
        src={paymentBillSrc}
        alt={renderIntlLabel('order_crs_label_fukuan_order_preview')}
      />
    </>
  );
});

export default InviteContent;
